<script>
import { onMount } from 'svelte';
import { ioRevealInit, ioSectionsInit, monthRefs } from '../src/common.js';
import metricsData from '../data/metrics/index.json';
import settings from '../setup/settings.json'
import SectionIntro from './components/SectionIntro.svelte';
import SectionAbout from './components/SectionAbout.svelte';
import SectionConclusion from './components/SectionConclusion.svelte';
import SectionContext from './components/SectionContext.svelte';
import SectionPercentChange from './components/SectionPercentChange.svelte';
import SectionTwoFigures from './components/SectionTwoFigures.svelte';
import SectionMonthsFigures from './components/SectionMonthsFigures.svelte';
import SectionChart from './components/SectionChart.svelte';
import SectionTop10Stations from './components/SectionTop10Stations.svelte';
import SectionBubbleMap from './components/SectionBubbleMap.svelte';
import SectionLineMap from './components/SectionLineMap.svelte';

let appContainer;
let introTarget;
let sectionInView = { target: introTarget }
let urlParams = new URLSearchParams(window.location.search);
let dateRange = urlParams.get('range') ? urlParams.get('range') : 'jan_aug'
let dateRangeFormatted = dateRange
													.split("_")
													.map(s => monthRefs.find(m => m.abbr === s).name)
													.join(" to ")
console.log("metricsData", metricsData)


// prep the data
let timeDataDailyTrips = {
	series2019: metricsData.daily_trip_totals['2019'].byrange[dateRange],
	series2020: metricsData.daily_trip_totals['2020'].byrange[dateRange],
	color2019: '#18C7DE',
	color2020: '#e5ff00'
}
let timeDataDailyAvgDurations = {
	series2019: metricsData.daily_avg_trip_durations['2019'].byrange[dateRange].map(x => {
		x.avgdailyduration = Number(x.duration)/60
		return x
	}),
	series2020: metricsData.daily_avg_trip_durations['2020'].byrange[dateRange].map(x => {
		x.avgdailyduration = Number(x.duration)/60
		return x
	}),
	color2019: '#18C7DE',
	color2020: '#e5ff00'
}
let popularStartStations = {
	data2019: {
		all: metricsData.popular_start_stations['2019'].byrange[dateRange],
		bymonth: metricsData.popular_start_stations['2019'].bymonth
	},
	data2020: {
		all: metricsData.popular_start_stations['2020'].byrange[dateRange],
		bymonth: metricsData.popular_start_stations['2020'].bymonth
	},
}

let popularEndStations = {
	data2019: {
		all: metricsData.popular_end_stations['2019'].byrange[dateRange],
		bymonth: metricsData.popular_end_stations['2019'].bymonth
	},
	data2020: {
		all: metricsData.popular_end_stations['2020'].byrange[dateRange],
		bymonth: metricsData.popular_end_stations['2020'].bymonth
	},
}

let popularStartToEndStations = {
	data2019: {
		all: metricsData.popular_start_to_end_stations['2019'].byrange[dateRange],
		bymonth: metricsData.popular_start_to_end_stations['2019'].bymonth
	},
	data2020: {
		all: metricsData.popular_start_to_end_stations['2020'].byrange[dateRange],
		bymonth: metricsData.popular_start_to_end_stations['2020'].bymonth
	},
}


const gotoNextSection = () => {
	if (!sectionInView.target) {
		return
	}
	sectionInView.target.scrollIntoView({ behavior: 'smooth' });
  // console.log("gotoNextSection -> sectionInView.target", sectionInView.target)
}

const handleKeydown = (event) => {
	if (event.keyCode === 32) {
		event.preventDefault();
		gotoNextSection()
	}
}

onMount(async () => {
	ioSectionsInit(sectionInView);
	ioRevealInit(appContainer);
});

</script>

<svelte:window on:keydown={handleKeydown}/>

<main bind:this="{appContainer}">
	<img src="https://uk.steergroup.com/themes/custom/steer/logo.png" alt="Steer" class="app-logo">
	<div
		on:click="{() => gotoNextSection() }"
		style="z-index: 1999; position: fixed; left: 10px; bottom:10px">
		<i class="fas fa-arrow-down"></i>
	</div>

	<SectionIntro bind:this="{introTarget}"></SectionIntro>

	<SectionContext bgCssClass="bg-primary has-text-white"></SectionContext>

	<!-- Trip numbers START -->
	<SectionTwoFigures
		bgCssClass="bg-tertiary has-text-white"
		iconClass='fas fa-bicycle'
		title='Total Trips'
		description='{"Total Cycle Hire trips between " + dateRangeFormatted}'
		figure2019='{metricsData.totals.byrange[dateRange].totaltrips2019}'
		figure2020='{metricsData.totals.byrange[dateRange].totaltrips2020}'
		units=''
		decimalPlaces=1
	/>
	<SectionPercentChange
		iconClass='fas fa-bicycle'
		iconClass2='fas fa-arrow-down'
		title='Total Trips'
		figure='{100 * (1-(Number(metricsData.totals.byrange[dateRange].totaltrips2020)/Number(metricsData.totals.byrange[dateRange].totaltrips2019)))}'
		decimalPlaces=0
		>
		<p slot="description">Drop in the number of Cycle Hire trips from <strong>2019</strong> to <strong>2020</strong> across {dateRangeFormatted}.</p>
	</SectionPercentChange>
	<SectionMonthsFigures
		bgCssClass="bg-tertiary has-text-white"
		iconClass='fas fa-bicycle'
		title='Total Trips'
		data='{metricsData.totals.bymonth}'
		totalProp="totaltrips"
		units=''
		decimalPlaces=0>
	<p slot="description">Total Cycle Hire trips for <strong>each month</strong>.</p>
	</SectionMonthsFigures>
	<SectionChart
		bgCssClass="bg-secondary has-text-white"
		title="Daily Trips"
		iconClass='fas fa-bicycle'
		data="{timeDataDailyTrips}" />


	<!-- Trip times START -->
	<SectionTwoFigures
		bgCssClass="bg-tertiary has-text-white"
		iconClass='fas fa-clock'
		title='Trip Times'
		description='{"Total cumulative Cycle Hire trip durations " + dateRangeFormatted +"."}'
		figure2019='{metricsData.totals.byrange[dateRange].totalduration2019/60}'
		figure2020='{metricsData.totals.byrange[dateRange].totalduration2020/60}'
		units='min'
		decimalPlaces=0
	/>
	<SectionTwoFigures
		bgCssClass="bg-tertiary has-text-white"
		iconClass='fas fa-clock'
		title='Trip Times'
		description='{"Average Cycle Hire trip duration " + dateRangeFormatted +"."}'
		figure2019='{metricsData.totals.byrange[dateRange].avgduration2019/60}'
		figure2020='{metricsData.totals.byrange[dateRange].avgduration2020/60}'
		units='min'
		decimalPlaces=0
	/>
	<SectionMonthsFigures
		bgCssClass="bg-tertiary has-text-white"
		iconClass='fas fa-bicycle'
		title='Trip Times'
		data='{metricsData.totals.bymonth}'
		units='min'
		totalProp="avgduration"
		decimalPlaces=0>
	<p slot="description">Average Cycle Hire trip duration for <strong>each month</strong>.</p>
	</SectionMonthsFigures>
	<SectionChart
		bgCssClass="bg-secondary has-text-white"
		title="Daily Average Trip Duration"
		iconClass='fas fa-clock'
		datefield='tripstartdate'
		yaxisLabel="Trip Duration (min)"
		valuefield='avgdailyduration'
		data="{timeDataDailyAvgDurations}" />
	<!-- Trip times END -->



	<!-- Popular Stations START -->
	<SectionTop10Stations
		bgCssClass="bg-secondary has-text-white"
		title="Popular Stations"
		iconClass='fas fa-bicycle'
		stationIdField='startstationid'
		countfield='count'
		data="{popularStartStations}">
	 <p> List of most popular stations to <strong class="highlight">start</strong> cycle trips {dateRangeFormatted}.</p>
	</SectionTop10Stations>
	<SectionBubbleMap
		mapid="bubblemap1"
		data="{popularStartStations.data2019}"
		markerColor="#18C7DE"
		countfield='count'
		title="2019 Popular Start Stations"
		bottomMargin="true"
		/>
	<SectionBubbleMap
		mapid="bubblemap2"
		data="{popularStartStations.data2020}"
		markerColor="#e5ff00"
		countfield='count'
		title="2020 Popular Start Stations" />

	<SectionTop10Stations
		bgCssClass="bg-secondary has-text-white"
		title="Popular Stations"
		iconClass='fas fa-bicycle'
		stationIdField='endstationid'
		countfield='count'
		data="{popularEndStations}">
	 <p> List of most popular stations to <strong class="highlight">end</strong> cycle trips {dateRangeFormatted}.</p>
	</SectionTop10Stations>
	<SectionBubbleMap
		mapid="bubblemap3"
		stationIdField="endstationid"
		data="{popularEndStations.data2019}"
		markerColor="#18C7DE"
		countfield='count'
		title="2019 Popular End Stations"
		bottomMargin="true"/>
	<SectionBubbleMap
		mapid="bubblemap4"
		stationIdField="endstationid"
		data="{popularEndStations.data2020}"
		countfield='count'
		markerColor="#e5ff00"
		title="2020 Popular End Stations" />


	<SectionTop10Stations
		bgCssClass="bg-secondary has-text-white"
		title="Popular Stations"
		iconClass='fas fa-bicycle'
		stationIdField='id'
		countfield='count'
		topNo='5'
		bothStations="{true}"
		data="{popularStartToEndStations}">
	 <p> List of most popular station to station <strong class="highlight">routes</strong>  {dateRangeFormatted}.</p>
	</SectionTop10Stations>
	<SectionLineMap
		mapid="linemap1"
		color="#18C7DE"
		data="{popularStartToEndStations.data2019}"
		title="2019 top 200 routes"
		bottomMargin="true"/>
	<SectionLineMap
		mapid="linemap2"
		color="#ffbb00"
		countfield='count'
		data="{popularStartToEndStations.data2020}"
		title="2020 top 200 routes"
		bottomMargin="true"/>
	<!-- Popular Stations END -->

	<SectionConclusion></SectionConclusion>
	<SectionAbout></SectionAbout>


</main>

<style>
.app-logo {
  position: fixed;
  bottom: 20px;
  right: 20px;
	width: 90px;
	z-index: 1;
}

@media (min-width: 640px) {
	main {
		max-width: none;
	}
}
</style>